<script setup lang="ts">
import { apiAnalytics } from '@api/analytics';
import CloseIcon from '@assets/icons/close-round.svg?component';
import BaseButton from '@components/quiz_v2/BaseButton/BaseButton.vue';
import BaseDialog from '@components/quiz_v2/BaseDialog/BaseDialog.vue';
import BaseInput from '@components/quiz_v2/BaseInput/BaseInput.vue';
import { pushDataLayerEvent } from '@helpers/dataLayer';
import { useUserStore } from '@stores/index';
import { useVuelidate } from '@vuelidate/core';
import { email as emailValidator, required } from '@vuelidate/validators';
import { useLocalStorage } from '@vueuse/core';
import { differenceInDays } from 'date-fns';
import { computed, onMounted, reactive, ref } from 'vue';

const STORAGE_KEY = 'email-pop-up';
const DELAY_MS = 15000;
const HIDE_DAYS = 7;

interface StorageValue {
  visibility: boolean;
  date: string | null;
}

defineProps<{
  email?: string;
  heading?: string;
  description?: string;
  disclaimer?: string;
  headingSuccess?: string;
  descriptionSuccess?: string;
  disclaimerSuccess?: string;
  image?: string;
}>();

const userStore = useUserStore();

const formData = reactive({ email: '' });
const formValidation = useVuelidate(
  { email: { required, email: emailValidator } },
  formData,
  { $scope: false },
);

const isOpen = ref(false);
const submitLoading = ref(false);
const isSuccess = ref(false);

const storage = useLocalStorage<StorageValue>(STORAGE_KEY, { visibility: false, date: null });

const shouldShow = computed(() => {
  if (storage.value.visibility) return false;

  if (storage.value.date) {
    const diffDays = differenceInDays(new Date(), new Date(storage.value.date));
    if (diffDays < HIDE_DAYS) return false;
  }

  if (!userStore.info?.isGuest && userStore.profileInfo?.user.consentEmailGeneralMarketing === true) {
    return false;
  }

  return true;
});

const handleClose = () => {
  if (!storage.value.visibility) {
    storage.value = { visibility: false, date: new Date().toISOString() };
  }
  formData.email = '';
  isOpen.value = false;
};

const submit = async () => {
  const isValid = await formValidation.value.$validate();
  if (!isValid) return;

  try {
    submitLoading.value = true;

    await apiAnalytics.track({
      event: 'NewsletterSubscribe',
      payload: {
        source: 'site_popup',
        email: formData.email,
      },
    });

    pushDataLayerEvent('MarketingGenericConsent', formData.email).catch(() => {});

    isSuccess.value = true;
    storage.value = { visibility: true, date: new Date().toISOString() };
    formData.email = '';
  } catch {
    // Non-blocking.
  } finally {
    submitLoading.value = false;
  }
};

onMounted(() => {
  if (!shouldShow.value) return;

  const timer = setTimeout(() => {
    clearTimeout(timer);
    if (shouldShow.value) isOpen.value = true;
  }, DELAY_MS);
});
</script>

<template>
  <BaseDialog
    v-model:visibility="isOpen"
    :close-button="false"
    content-class="max-w-375 lg:max-w-700 !p-0 overflow-hidden"
    @update:visibility="(value) => { if (!value) handleClose(); }"
  >
    <div class="relative grid min-h-480 bg-white lg:min-h-500 lg:grid-cols-2">
      <BaseButton
        class="absolute top-16 -right-8 z-1 inline-flex size-32 items-center justify-center rounded-full bg-shell text-off-black-500 lg:size-40"
        data-testpl="email-popup-close"
        @click="handleClose"
      >
        <CloseIcon class="size-16" />
      </BaseButton>

      <div
        v-if="!isSuccess"
        class="flex flex-col justify-between p-24 pt-56 lg:px-32 lg:pb-16 lg:pt-40"
      >
        <div>
          <h2
            v-if="heading"
            class="text-21 font-secondary font-medium lg:text-32"
            data-testpl="email-popup-title"
          >
            {{ heading }}
          </h2>
          <p
            v-if="description"
            class="text-14 text-secondary-text mt-16 leading-normal lg:mt-24"
            data-testpl="email-popup-description"
          >
            {{ description }}
          </p>
        </div>

        <div>
          <div class="mt-24 flex flex-col gap-16 lg:mt-32" data-testpl="email-popup-form">
            <BaseInput
              v-model.trim="formData.email"
              type="email"
              name="email"
              placeholder="Email address"
              autocomplete="off"
              data-testpl="email-popup-email-input"
              :error="formValidation.email.$errors?.[0]?.$message"
            />

            <BaseButton
              :loading="submitLoading"
              class="w-full"
              data-testpl="email-popup-sign-up"
              @click="submit"
            >
              Sign up for email
            </BaseButton>
          </div>

          <p
            v-if="disclaimer"
            class="text-10 text-secondary-text mt-16 leading-normal"
            data-testpl="email-popup-disclaimer"
          >
            {{ disclaimer }}
          </p>
        </div>
      </div>

      <div
        v-else
        class="flex flex-col justify-between p-24 pt-56 lg:px-32 lg:pb-16 lg:pt-40"
      >
        <div>
          <h2
            v-if="headingSuccess"
            class="text-21 font-secondary font-medium lg:text-32"
            data-testpl="email-popup-title"
          >
            {{ headingSuccess }}
          </h2>
          <p
            v-if="descriptionSuccess"
            class="text-14 text-secondary-text mt-16 leading-normal lg:mt-24"
            data-testpl="email-popup-description"
          >
            {{ descriptionSuccess }}
          </p>
        </div>

        <div>
          <BaseButton class="w-full" data-testpl="email-popup-continue" @click="handleClose">
            Continue
          </BaseButton>

          <p
            v-if="disclaimerSuccess"
            class="text-10 text-secondary-text mt-16 leading-normal"
            data-testpl="email-popup-disclaimer"
          >
            {{ disclaimerSuccess }}
          </p>
        </div>
      </div>

      <figure v-if="image" class="absolute top-0 -right-24 -bottom-24 hidden w-1/2 lg:block">
        <img :src="image" alt="" class="size-full object-cover">
      </figure>
    </div>
  </BaseDialog>
</template>
